// frontend/src/services/scientificDashboard.service.ts

import api from './api';
import type { PipelineMetric } from '../interfaces/pipeline.interface';

export interface TraceItem {
  trace_id: string;
  dataset_name?: string;
  pipeline_version?: number;
  vendor?: string;
  created_at?: string;
  status?: string;
}

export class ScientificDashboardService {

  static async getFullStory(traceId: string): Promise<{
    trace_id: string;
    dcp: any | null;
    eda_explore: any | null;
    eda_prepare: any | null;
    ml_experiments: any[];
    know: any[];
    pipeline: PipelineMetric[];
  }> {
    const [dcp, edaExplore, edaPrepare, mlExperiments, know, pipeline] = await Promise.all([
      api.get(`/analysis/dataset/trace/${traceId}`).catch(() => ({ data: null })),
      api.get(`/eda/snapshot/trace/${traceId}`).catch(() => ({ data: [] })),
      api.get(`/eda-prepare/snapshot/trace/${traceId}`).catch(() => ({ data: [] })),
      api.get(`/ml/experiments/trace/${traceId}`).catch(() => ({ data: [] })),
      api.get(`/know/recommendation/trace/${traceId}`).catch(() => ({ data: [] })),
      api.get(`/pipeline/execucao/${traceId}`).catch(() => ({ data: [] })),
    ]);

    const edaExploreData = edaExplore.data?.data?.[0] ?? null;
    const edaPrepareData = edaPrepare.data?.data?.[0] ?? null;

    return {
      trace_id: traceId,
      dcp: dcp.data?.data ?? null,
      eda_explore: edaExploreData,
      eda_prepare: edaPrepareData,
      ml_experiments: mlExperiments.data?.data ?? [],
      know: know.data?.data ?? [],
      pipeline: pipeline.data?.data ?? [],
    };
  }

  static async getAvailableTraces(): Promise<TraceItem[]> {
    const response = await api.get('/pipeline/historico');
    const linhas: PipelineMetric[] = response.data.data ?? [];
    return linhas
      .map((l: any) => ({
        trace_id: l.trace_id,
        dataset_name: l.dataset_name,
        pipeline_version: l.pipeline_version,
        vendor: l.vendor,
        created_at: l.created_at,
        status: l.status,
      }))
      .filter(l => Boolean(l.trace_id));
  }

  static async getScientificSummary(traceId: string): Promise<any> {
    const fullStory = await this.getFullStory(traceId);
    return this.buildSummary(fullStory);
  }

  private static buildSummary(fullStory: any): any {
    const { dcp, eda_explore, eda_prepare, ml_experiments, know, pipeline } = fullStory;

    const startEvent = (pipeline || []).find(
      (p: any) => p.stage === 'orquestracao' && p.event_type === 'request'
    );
    const start = startEvent?.payload ?? null;

    const dcpBlock = dcp ? {
      dataset_name: dcp.dataset_name ?? null,
      total_rows: dcp.total_rows ?? null,
      total_columns: dcp.total_columns ?? null,
      memory_mb: dcp.memory_mb ?? null,
      file_size_mb: dcp.file_size_mb ?? null,
      columns_metadata: dcp.columns_metadata ?? [],
      null_analysis: dcp.null_analysis ?? null,
      dataset_hash: dcp.dataset_hash ?? null,
      schema_hash: dcp.schema_hash ?? null,
      artifact_path: dcp.artifact_path ?? null,
      trace_id: dcp.trace_id ?? null,
      pipeline_version: dcp.pipeline_version ?? null,
      vendor: dcp.vendor ?? null,
      created_at: dcp.created_at ?? null,
    } : null;

    const edaExploreBlock = eda_explore ? {
      id: eda_explore.id ?? null,
      trace_id: eda_explore.trace_id ?? null,
      dataset_name: eda_explore.dataset_name ?? null,
      target_name: eda_explore.target_name ?? null,
      rows_count: eda_explore.rows_count ?? null,
      columns_count: eda_explore.columns_count ?? null,
      majority_class_ratio: eda_explore.majority_class_ratio ?? null,
      baseline_accuracy: eda_explore.baseline_accuracy ?? null,
      baseline_auc: eda_explore.baseline_auc ?? null,
      max_separability: eda_explore.max_separability ?? null,
      numeric_columns: eda_explore.numeric_columns ?? null,
      categorical_columns: eda_explore.categorical_columns ?? null,
      identifier_columns: eda_explore.identifier_columns ?? null,
      schema: eda_explore.schema_json ?? null,
      statistics: eda_explore.statistics_json ?? null,
      target_distribution: eda_explore.target_distribution_json ?? null,
      separability: eda_explore.separability_json ?? null,
      warnings: eda_explore.warnings_json ?? null,
      created_at: eda_explore.created_at ?? null,
    } : null;

    const edaPrepareBlock = eda_prepare ? {
      id: eda_prepare.id ?? null,
      trace_id: eda_prepare.trace_id ?? null,
      dataset_name: eda_prepare.dataset_name ?? null,
      target_name: eda_prepare.target_name ?? null,
      problem_type: eda_prepare.problem_type ?? null,
      metric: eda_prepare.metric ?? null,
      split_strategy: eda_prepare.split_strategy ?? null,
      n_features: eda_prepare.n_features ?? null,
      dropped_features_count: eda_prepare.dropped_features_count ?? null,
      forced_drops_count: eda_prepare.forced_drops_count ?? null,
      train_config: eda_prepare.train_config_json ?? null,
      feature_schema: eda_prepare.feature_schema_json ?? null,
      physical_schema: eda_prepare.physical_schema_json ?? null,
      transformations: eda_prepare.transformations_json ?? null,
      warnings: eda_prepare.warnings_json ?? null,
      schema: eda_prepare.schema_json ?? null,
      statistics: eda_prepare.statistics_json ?? null,
      created_at: eda_prepare.created_at ?? null,
    } : null;

    const mlBlock = (ml_experiments || []).length > 0 ? {
      experiments: ml_experiments,
      approved: ml_experiments.filter((e: any) => e.decision === 'APPROVED'),
      rejected: ml_experiments.filter((e: any) => e.decision === 'REJECTED'),
    } : null;

    const knowBlock = (know || []).length > 0 ? {
      recommendations: know,
    } : null;

    const executionBlock = (pipeline || []).length > 0 ? {
      pipeline,
    } : null;

    // ============================================================
    // EXTRAI RELATÓRIOS DO PAYLOAD SALVO PELO ORC
    // Estrutura real (evidência do banco):
    //   payload.data.rpt_business.data.report_path
    //   payload.data.ml.report_scientific
    // ============================================================
    const orcResponseLine = (pipeline || []).find(
      (p: any) => p.stage === 'orquestracao' && p.event_type === 'response'
    );
    const orcPayload = orcResponseLine?.payload ?? null;

    const rptBusinessBlock = orcPayload?.data?.rpt_business?.data?.report_path
      ? { report_path: orcPayload.data.rpt_business.data.report_path }
      : null;

    const rptScientificBlock = orcPayload?.data?.ml?.report_scientific
      ? { report_path: orcPayload.data.ml.report_scientific }
      : null;

    return {
      trace_id: fullStory.trace_id,
      start,
      know: knowBlock,
      dcp: dcpBlock,
      eda_explore: edaExploreBlock,
      eda_prepare: edaPrepareBlock,
      ml: mlBlock,
      execution: executionBlock,
      rpt_business: rptBusinessBlock,
      rpt_scientific: rptScientificBlock,
    };
  }
}